import { request } from "./client";
import type {
  ImputationRule,
  ImputationRuleApplyResult,
  ImputationRuleInput,
} from "./types";

/** GET /api/v1/tawa/imputation-rules : règles du tenant et des salariés gérés. */
export async function listImputationRules(): Promise<{
  rules: ImputationRule[];
}> {
  return request<{ rules: ImputationRule[] }>({
    method: "GET",
    url: "/imputation-rules",
  });
}

/** POST /api/v1/tawa/imputation-rules : `worker_url` absent, règle de tous. */
export async function createImputationRule(
  body: ImputationRuleInput,
): Promise<ImputationRule> {
  return request<ImputationRule>({
    method: "POST",
    url: "/imputation-rules",
    data: body,
  });
}

/** PATCH /api/v1/tawa/imputation-rules/{id}. */
export async function updateImputationRule(
  id: string,
  body: Partial<Pick<ImputationRule, "active" | "match_value" | "project_id">>,
): Promise<ImputationRule> {
  return request<ImputationRule>({
    method: "PATCH",
    url: `/imputation-rules/${encodeURIComponent(id)}`,
    data: body,
  });
}

/** DELETE /api/v1/tawa/imputation-rules/{id}. */
export async function deleteImputationRule(id: string): Promise<void> {
  await request<void>({
    method: "DELETE",
    url: `/imputation-rules/${encodeURIComponent(id)}`,
  });
}

export interface ApplyRulesBody {
  from: string;
  to: string;
  worker_url?: string;
  dry_run: boolean;
  cursor?: string;
  /** Règle tout juste créée : seule à reprendre le temps qu'une règle moins
   * précise du même dépôt avait imputé. */
  rule_id?: string;
}

/** POST /api/v1/tawa/imputation-rules/apply : 366 jours au plus, bornes
 * incluses, 25 salariés au plus par appel. `dry_run` rend le bilan sans rien
 * écrire. */
export async function applyImputationRules(
  body: ApplyRulesBody,
): Promise<ImputationRuleApplyResult> {
  return request<ImputationRuleApplyResult>({
    method: "POST",
    url: "/imputation-rules/apply",
    data: body,
  });
}

/** Cumule deux pages d'application : compteurs additionnés, règles fusionnées. */
export function mergeApplyResults(
  acc: ImputationRuleApplyResult | null,
  page: ImputationRuleApplyResult,
): ImputationRuleApplyResult {
  if (!acc) return { ...page, rules: page.rules.map((r) => ({ ...r })) };
  const rules = acc.rules.map((r) => ({ ...r }));
  for (const r of page.rules) {
    const prev = rules.find((x) => x.rule_id === r.rule_id);
    if (!prev) {
      rules.push({ ...r });
      continue;
    }
    prev.activities += r.activities;
    prev.worked_minutes += r.worked_minutes;
    prev.skipped_locked += r.skipped_locked;
    prev.skipped_locked_minutes += r.skipped_locked_minutes;
    prev.already_applied += r.already_applied;
  }
  return {
    ...page,
    workers: acc.workers + page.workers,
    activities: acc.activities + page.activities,
    worked_minutes: acc.worked_minutes + page.worked_minutes,
    skipped_locked: acc.skipped_locked + page.skipped_locked,
    skipped_locked_minutes:
      acc.skipped_locked_minutes + page.skipped_locked_minutes,
    already_applied: acc.already_applied + page.already_applied,
    workers_scanned: acc.workers_scanned + page.workers_scanned,
    rules,
  };
}

/** Échec en cours d'application : `done` cumule les pages déjà écrites (null si
 * aucune). */
export class ApplyRulesInterrupted extends Error {
  constructor(
    readonly done: ImputationRuleApplyResult | null,
    readonly reason: unknown,
  ) {
    super("imputation rules apply interrupted");
  }
}

/** Enchaîne les pages jusqu'au dernier salarié. `onPage` reçoit le cumul. */
export async function applyImputationRulesAll(
  body: Omit<ApplyRulesBody, "cursor">,
  onPage: (acc: ImputationRuleApplyResult) => void,
): Promise<ImputationRuleApplyResult> {
  let acc: ImputationRuleApplyResult | null = null;
  let cursor: string | undefined;
  for (;;) {
    let page: ImputationRuleApplyResult;
    try {
      page = await applyImputationRules({ ...body, cursor });
    } catch (e) {
      throw new ApplyRulesInterrupted(acc, e);
    }
    acc = mergeApplyResults(acc, page);
    onPage(acc);
    if (!page.partial || !page.next_cursor) return acc;
    cursor = page.next_cursor;
  }
}
